import { sourceParam, type TranscriptionSource } from '@/lib/transcription-source';

/**
 * Non-visual rules of the "Transcrire" session, kept pure so tests lock the
 * payloads sent to transcriptionApi and the batch transitions. Behaviour is
 * the page's historical one, moved here unchanged.
 */

/** Audios requested per batch from transcriptionApi.getPendingAudios. */
export const TRANSCRIPTION_AUDIO_PAGE_SIZE = 5;

/**
 * Query of getPendingAudios. The server rotates the queue: page stays 1 and
 * every audio already shown or sent this session is excluded.
 */
export function pendingAudiosQuery({
  lang,
  seenIds,
  submittedIds,
  rotationId,
  source,
}: {
  lang: string;
  seenIds: Iterable<string>;
  submittedIds: Iterable<string>;
  rotationId: string | null;
  source: TranscriptionSource;
}) {
  return {
    lang,
    page: 1,
    limit: TRANSCRIPTION_AUDIO_PAGE_SIZE,
    excludeIds: Array.from(new Set([...seenIds, ...submittedIds])),
    rotationId,
    source: sourceParam(source),
  };
}

/** The rotation id handed back by the server, when it sent one. */
export const rotationIdOf = (response: unknown): string | null => {
  const value = (response as Record<string, unknown> | null)?.rotationId;
  return typeof value === 'string' ? String(value) : null;
};

/** Whether another batch can be asked for after this one. */
export function hasMoreAudios(total: number | null, seenCount: number, batchLength: number): boolean {
  return typeof total === 'number' ? seenCount < total && batchLength > 0 : batchLength === TRANSCRIPTION_AUDIO_PAGE_SIZE;
}

/** Body of transcriptionApi.add: both texts trimmed, the French one only when filled. */
export const transcriptionPayload = (audioId: string | number, text: string, textFrancais: string) => ({
  audioId,
  text: text.trim(),
  textFrancais: textFrancais.trim() || undefined,
});

/** Body of transcriptionApi.release when an audio is skipped. */
export const releasePayload = (audioId: string | number, rotationId: string | null) => ({ audioId, rotationId });

/**
 * Why a submit stops before reaching the API, in the historical order:
 * empty text, a submit already running, then the validation gate.
 */
export function submitBlocker({
  hasCurrent,
  text,
  isSubmitting,
  gateBlocked,
}: {
  hasCurrent: boolean;
  text: string;
  isSubmitting: boolean;
  gateBlocked: boolean;
}): 'textRequired' | 'busy' | 'gate' | null {
  if (!hasCurrent || !text.trim()) return 'textRequired';
  if (isSubmitting) return 'busy';
  if (gateBlocked) return 'gate';
  return null;
}

/** After a submit or a skip: next audio of the batch, next batch, or the end. */
export function nextAudioStep(currentIndex: number, batchLength: number, hasMore: boolean): 'advance' | 'nextBatch' | 'end' {
  if (currentIndex < batchLength - 1) return 'advance';
  return hasMore ? 'nextBatch' : 'end';
}
